import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { CheckCircle2, Lock, GraduationCap, ArrowRight, ClipboardList, Clock, Briefcase } from "lucide-react";
import { AppHeader } from "../../components/AppHeader";
import { ProgressTracker } from "../../components/ProgressTracker";
import { Button } from "../../components/ui/button";
import api from "../../lib/api";

const WELCOME_PARAS = [
  "Thank you for your interest in contracting with GIC&E Inc.",
  "Our work requires more than a passion for helping people. Individuals providing services through GIC&E must demonstrate appropriate education, professional judgment, ethical boundaries, documentation competency, accountability, and the ability to translate an individualized treatment plan into meaningful services.",
  "This assessment helps us determine whether your qualifications and current competencies align with the services provided through GIC&E.",
  "Completion of this assessment does not constitute an offer of employment, contractor agreement, guarantee of work, or guarantee of advancement in the selection process.",
  "Please answer all questions independently and accurately.",
];

export default function ApplicantHome() {
  const [app, setApp] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/application/me");
      if (!["ALIGNED", "UNSURE"].includes(data.culture_alignment)) {
        navigate("/culture", { replace: true });
        return;
      }
      setApp(data);
    } catch { toast.error("Could not load your application."); }
  };
  useEffect(() => { load(); }, []);

  if (!app) return <div className="min-h-screen bg-[#FAF9F6]" />;

  const notStarted = ["Application Received", "Pre-Screen Invitation Sent"].includes(app.status);
  const inProgress = app.status === "Pre-Screen In Progress" || app.status === "Additional Information Requested";
  const submitted = app.locked && !app.training_unlocked;

  const beginTraining = () => navigate("/training");
  const beginOnboarding = () => navigate("/onboarding");

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <AppHeader />
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:grid lg:grid-cols-[280px_1fr] lg:gap-10">
        {/* Progress sidebar */}
        <aside className="mb-8 lg:mb-0">
          <div className="lg:sticky lg:top-24 rounded-xl border border-[#E5E2DC] bg-white p-6">
            <h3 className="mb-5 font-heading text-base font-semibold text-[#1F2924]">Your Progress</h3>
            <ProgressTracker status={app.status} />
          </div>
        </aside>

        {/* Main */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="space-y-6">
          <div>
            <p className="text-xs uppercase tracking-[0.14em] text-[#515E58]">Mental &amp; Behavioral Health Contractor</p>
            <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight text-[#1F2924] sm:text-4xl">
              Pre-Screening &amp; Competency Assessment
            </h1>
          </div>

          {(notStarted || inProgress) && (
            <div className="rounded-xl border border-[#E5E2DC] bg-white p-8">
              <h2 className="font-heading text-2xl font-semibold text-[#1F2924]">Welcome.</h2>
              {WELCOME_PARAS.map((p, i) => (
                <p key={i} className="mt-4 leading-relaxed text-[#515E58]">{p}</p>
              ))}
              <div className="mt-6 flex items-center gap-2 rounded-lg bg-[#EAF0ED] px-4 py-3 text-sm text-[#2C5545]">
                <Clock className="h-4 w-4" /> Estimated completion time: 30–45 minutes. You can save and return later.
              </div>
              <Button onClick={() => navigate("/assessment")} data-testid="begin-assessment-btn"
                className="mt-6 gap-2 rounded-lg bg-[#2C5545] px-8 py-6 text-base font-semibold hover:bg-[#224336] transition-transform hover:-translate-y-[1px]">
                <ClipboardList className="h-5 w-5" />
                {inProgress ? "Continue Assessment" : "Begin Assessment"}
                <ArrowRight className="h-5 w-5" />
              </Button>
            </div>
          )}

          {submitted && (
            <div className="rounded-xl border border-[#3A7D5E]/30 bg-[#EEF5F0] p-8" data-testid="assessment-received">
              <CheckCircle2 className="h-10 w-10 text-[#3A7D5E]" />
              <h2 className="mt-4 font-heading text-2xl font-semibold text-[#1F2924]">Assessment Received</h2>
              <p className="mt-3 leading-relaxed text-[#515E58]">
                Thank you for completing the GIC&E Pre-Screening &amp; Competency Assessment. Your information has been
                submitted for review. Our team will review your qualifications, professional experience, judgment,
                documentation competency and applied case response.
              </p>
              <p className="mt-3 leading-relaxed text-[#515E58]">
                If selected to advance, you will receive instructions regarding the next phase of the GIC&E contractor
                selection and training process.
              </p>
            </div>
          )}

          {/* Training portal card */}
          <div className={`rounded-xl border p-8 ${app.training_unlocked ? "border-[#2C5545]/30 bg-white" : "border-[#E5E2DC] bg-[#F4F2EC]"}`}
            data-testid="training-card">
            <div className="flex items-start gap-4">
              <div className={`flex h-12 w-12 items-center justify-center rounded-lg ${app.training_unlocked ? "bg-[#2C5545] text-white" : "bg-[#E5E2DC] text-[#515E58]"}`}>
                {app.training_unlocked ? <GraduationCap className="h-6 w-6" /> : <Lock className="h-6 w-6" />}
              </div>
              <div className="flex-1">
                <h2 className="font-heading text-xl font-semibold text-[#1F2924]">GIC&E Contractor Training</h2>
                {!app.training_unlocked ? (
                  <p className="mt-2 text-[#515E58]">
                    Training unlocks only after GIC&E reviews your assessment and approves your advancement.
                    You cannot access training until then.
                  </p>
                ) : app.training_completed_at ? (
                  <p className="mt-2 text-[#3A7D5E]">You have completed GIC&E contractor training. You are ready for the final interview / onboarding phase.</p>
                ) : app.training_started_at ? (
                  <>
                    <p className="mt-2 text-[#515E58]">Your training is in progress. Continue your lessons and pass the competency quiz to finish.</p>
                    <Button onClick={beginTraining} data-testid="continue-training-btn"
                      className="mt-4 gap-2 rounded-lg bg-[#2C5545] px-6 hover:bg-[#224336]">
                      <GraduationCap className="h-5 w-5" /> Continue Training
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="mt-2 text-[#3A7D5E]">Congratulations — you've advanced! Your training portal is now available.</p>
                    <Button onClick={beginTraining} data-testid="begin-training-btn"
                      className="mt-4 gap-2 rounded-lg bg-[#2C5545] px-6 hover:bg-[#224336]">
                      <GraduationCap className="h-5 w-5" /> Begin GIC&E Training
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
          {/* Employment onboarding card (post-hire) */}
          {app.onboarding_unlocked && (
            <div className="rounded-xl border border-[#2C5545]/30 bg-white p-8" data-testid="onboarding-card">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[#2C5545] text-white">
                  <Briefcase className="h-6 w-6" />
                </div>
                <div className="flex-1">
                  <h2 className="font-heading text-xl font-semibold text-[#1F2924]">New Hire Onboarding</h2>
                  {app.onboarding_completed_at ? (
                    <p className="mt-2 text-[#3A7D5E]">You've completed onboarding. Welcome to the GIC&E community!</p>
                  ) : (
                    <p className="mt-2 text-[#3A7D5E]">
                      Congratulations — you've been hired! Complete your documents, trainings, and probationary steps.
                    </p>
                  )}
                  <div className="mt-3 h-2 w-full max-w-sm overflow-hidden rounded-full bg-[#E5E2DC]">
                    <div className="h-full rounded-full bg-[#D56A47] transition-all"
                      style={{ width: `${Math.round((app.onboarding_progress / app.onboarding_total) * 100)}%` }} />
                  </div>
                  <div className="mt-1 text-xs text-[#515E58]">{app.onboarding_progress} of {app.onboarding_total} items complete</div>
                  <Button onClick={beginOnboarding} data-testid="begin-onboarding-btn"
                    className="mt-4 gap-2 rounded-lg bg-[#2C5545] px-6 hover:bg-[#224336]">
                    <Briefcase className="h-5 w-5" /> {app.onboarding_progress > 0 ? "Continue Onboarding" : "Begin Onboarding"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
}
